import { useSyncExternalStore } from 'react';
import { useMobileLayout } from '@/hooks/useMobileLayout.ts';
import { useUiStore } from '@/store/uiStore.ts';

// "Regular" in the iOS horizontal-size-class sense, as this app already defines it: not a phone
// AND wider than the compact window width the three preview grids centre under. Owner rule
// 2026-09-01 (items D + E) needs one predicate for "auto-scroll on entry is welcome here":
// macOS at a normal window size and iPad in .regular yes, iOS and iPad .compact no.
//
// Two halves, each deferring to the app's existing definition rather than inventing a new one:
//   • useMobileLayout — phones in any orientation (including Max-class landscape, which is wider
//     than 600px and would otherwise read as regular) plus the force-mobile M-key toggle, so
//     desktop emulation behaves like the phone it emulates.
//   • COMPACT_WIDTH_QUERY — iPad Split View / Slide Over, and a macOS window dragged narrow.
//     600px is the same boundary ProjectsView/SceneBoard/SceneBoardExpanded switch to centred
//     tracks at (`@media (max-width: 600px)`, pinned in gridCompactCentering.test.ts) and the
//     window-width-only sense of "compact" adaptiveGrid.ts documents. Keep the two in step —
//     useRegularWidthLayout.test.ts fails if they drift.
export const COMPACT_WIDTH_QUERY = '(max-width: 600px)';

const mql = typeof window !== 'undefined'
  ? window.matchMedia(COMPACT_WIDTH_QUERY)
  : null;

function subscribe(cb: () => void) {
  mql?.addEventListener('change', cb);
  return () => mql?.removeEventListener('change', cb);
}

function getSnapshot() {
  return mql?.matches ?? false;
}

/** True on regular-width layouts (macOS at a normal window size, iPad .regular); false on phones
 *  and on compact windows (iPad Split View / Slide Over, a narrowed macOS window). */
export function useRegularWidthLayout(): boolean {
  const isMobile = useMobileLayout();
  const compactWidth = useSyncExternalStore(subscribe, getSnapshot, () => false);
  return !isMobile && !compactWidth;
}

// ── Compact width alone (no phone clause) ────────────────────────────────────────────────
// Deliberately NOT the module-level `mql` singleton above. This pair backs useCompactWidth, which
// chooses a LAYOUT BRANCH rather than an enhancement — the Projects home renders its one library
// grid 3-up or 2-up on it — so it has to be right on the FIRST render in any host
// that installs or replaces window.matchMedia after this module is evaluated. The debug harness's
// viewport presets and the test suite both do exactly that, and an import-order-sensitive
// singleton makes the branch untestable without resetting the whole module graph. The cost is one
// matchMedia call per snapshot, which is nothing next to a wrong first paint.
//
// 20260903 — THE MediaQueryList 'change' EVENT IS NOT ENOUGH IN THIS WEBVIEW. An iPhone taken
// portrait → landscape → portrait on the Projects home came back rendering the WRONG branch: the
// WebView drops these deliveries across a rotation, exactly as useAdaptiveGrid.ts documents for
// ResizeObserver. Its idiom, copied here: window 'resize' as the second signal, plus
// 'orientationchange' re-fired on the NEXT animation frame because that event lands before the new
// layout. The snapshot re-reads matchMedia every time, so an extra notification that changes
// nothing is a free React bail-out.
function subscribeCompactWidth(cb: () => void) {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const live = window.matchMedia(COMPACT_WIDTH_QUERY);
  live.addEventListener('change', cb);
  let raf = 0;
  const notifyNextFrame = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(cb);
  };
  window.addEventListener('resize', cb);
  window.addEventListener('orientationchange', notifyNextFrame);
  return () => {
    live.removeEventListener('change', cb);
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', cb);
    window.removeEventListener('orientationchange', notifyNextFrame);
  };
}

function getCompactWidthSnapshot(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(COMPACT_WIDTH_QUERY).matches;
}

/** True on a COMPACT-WIDTH window — the same 600px boundary as above, and the force-mobile (M-key)
 *  emulation, but WITHOUT the phone clause useRegularWidthLayout composes in.
 *
 *  That omission is the point: a Max-class iPhone in landscape IS a phone, yet it is wider than
 *  600px and has room for three cards per row. Only the width decides here. */
export function useCompactWidth(): boolean {
  const compactWidth = useSyncExternalStore(subscribeCompactWidth, getCompactWidthSnapshot, () => false);
  const forceMobile = useUiStore(s => s.forceMobile);
  return compactWidth || forceMobile;
}
